import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import fr from 'date-fns/locale/fr';
import enUS from 'date-fns/locale/en-US';
import { addDays, addMinutes, addHours } from 'date-fns';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
  Divider,
  SelectChangeEvent,
  Box,
} from '@mui/material';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import TimerIcon from '@mui/icons-material/Timer';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

import { suspensionApi, SuspensionCreate } from '../../services/apiService';
import api from '../../services/apiService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Sensor {
  id: number;
  name: string | null;
  serial_number: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  preselectedSensorId?: number;
}

type Mode    = 'indefinite' | 'duration' | 'manual';
type DurUnit = 'minutes' | 'hours' | 'days';

// Date sentinelle pour "indéfini" : visible dans la BDD mais affichée "Indéfini" dans l'UI
export const INDEFINITE_END = '2099-12-31T23:59:59';

// ---------------------------------------------------------------------------
// Fix TZ : toISOString() convertit en UTC (-2h en été Paris UTC+2).
// On envoie une ISO locale sans offset : "2026-04-30T14:00:00"
// ---------------------------------------------------------------------------

function toLocalISOString(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SuspensionFormDialog: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
  preselectedSensorId,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith('fr') ? fr : enUS;

  // Capteur
  const [sensorId, setSensorId]      = useState<number | null>(preselectedSensorId ?? null);
  const [sensors, setSensors]        = useState<Sensor[]>([]);
  const [loadingSensors, setLoading] = useState(false);

  // Mode
  const [mode, setMode] = useState<Mode>('indefinite');

  // Mode durée
  const [durValue, setDurValue] = useState<number>(1);
  const [durUnit,  setDurUnit]  = useState<DurUnit>('hours');

  // Mode manuel — plage horaire
  const roundToNext5Min = () => {
    const n = new Date();
    return new Date(Math.ceil(n.getTime() / (5 * 60000)) * (5 * 60000));
  };
  const [startAt, setStartAt] = useState<Date | null>(roundToNext5Min());
  const [endAt,   setEndAt]   = useState<Date | null>(() => addHours(roundToNext5Min(), 1));

  // UI
  const [submitting,   setSubmitting]   = useState(false);
  const [serverError,  setServerError]  = useState<string | null>(null);

  // -------------------------------------------------------------------------
  // Load sensors
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (preselectedSensorId) return;
    setLoading(true);
    api.get<{ items: Sensor[] }>('/sensors/', { params: { limit: '500', active_only: 'true' } })
      .then((r) => setSensors(r.data.items))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [preselectedSensorId]);

  // -------------------------------------------------------------------------
  // Validation
  // -------------------------------------------------------------------------

  const validationError = (): string | null => {
    if (!sensorId) return t('suspensions.errors.sensorRequired');

    if (mode === 'duration') {
      if (!durValue || durValue <= 0) return t('suspensions.errors.durationRequired');
    }

    if (mode === 'manual') {
      if (!startAt || !endAt) return t('suspensions.errors.endBeforeStart');
      if (endAt <= startAt)   return t('suspensions.errors.endBeforeStart');
      if (startAt < new Date(Date.now() - 60_000)) return t('suspensions.errors.startInPast');
    }

    return null;
  };

  const validErr = validationError();

  // -------------------------------------------------------------------------
  // Submit
  // -------------------------------------------------------------------------

  const handleSubmit = async () => {
    if (validErr || !sensorId) return;
    setSubmitting(true);
    setServerError(null);

    try {
      let payload: SuspensionCreate;

      if (mode === 'indefinite') {
        payload = {
          sensor_id: sensorId,
          start_at:  toLocalISOString(new Date()),
          end_at:    INDEFINITE_END,
        };

      } else if (mode === 'duration') {
        const now = new Date();
        let end: Date;
        if      (durUnit === 'minutes') end = addMinutes(now, durValue);
        else if (durUnit === 'days')    end = addDays(now, durValue);
        else                            end = addHours(now, durValue);

        payload = {
          sensor_id: sensorId,
          start_at:  toLocalISOString(now),
          end_at:    toLocalISOString(end),
        };

      } else {
        // Plage ponctuelle
        payload = {
          sensor_id: sensorId,
          start_at:  toLocalISOString(startAt!),
          end_at:    toLocalISOString(endAt!),
        };
      }

      await suspensionApi.create(payload);
      onSuccess();
    } catch (err: any) {
      setServerError(err.message || t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------

  const ModeButton: React.FC<{
    value: Mode;
    icon: React.ReactNode;
    label: string;
    description: string;
  }> = ({ value, icon, label, description }) => {
    const selected = mode === value;
    return (
      <Box
        onClick={() => setMode(value)}
        sx={{
          flex: 1,
          minWidth: 120,
          p: 1.5,
          border: '2px solid',
          borderColor: selected ? 'primary.main' : 'divider',
          borderRadius: 2,
          cursor: 'pointer',
          bgcolor: selected ? 'primary.50' : 'background.paper',
          transition: 'all 0.15s',
          '&:hover': { borderColor: 'primary.light' },
        }}
      >
        <Stack alignItems="center" spacing={0.5}>
          <Box sx={{ color: selected ? 'primary.main' : 'text.secondary' }}>{icon}</Box>
          <Typography variant="body2" fontWeight={selected ? 700 : 400} color={selected ? 'primary.main' : 'text.primary'}>
            {label}
          </Typography>
          <Typography variant="caption" color="text.secondary" textAlign="center">
            {description}
          </Typography>
        </Stack>
      </Box>
    );
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t('suspensions.form.title')}</DialogTitle>

        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>

            {/* Erreur serveur */}
            {serverError && (
              <Alert severity="error" onClose={() => setServerError(null)}>
                {serverError}
              </Alert>
            )}

            {/* Sélection capteur */}
            {!preselectedSensorId && (
              <Autocomplete
                options={sensors}
                loading={loadingSensors}
                getOptionLabel={(o) => o.name ?? o.serial_number}
                value={sensors.find((s) => s.id === sensorId) ?? null}
                onChange={(_, val) => setSensorId(val?.id ?? null)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('suspensions.columns.sensor')}
                    required
                    size="small"
                  />
                )}
              />
            )}

            {/* ── Sélection du mode ── */}
            <Stack direction="row" spacing={1.5}>
              <ModeButton
                value="indefinite"
                icon={<AllInclusiveIcon />}
                label={t('suspensions.form.modeIndefinite')}
                description={t('suspensions.form.modeIndefiniteDesc')}
              />
              <ModeButton
                value="duration"
                icon={<TimerIcon />}
                label={t('suspensions.form.modeDuration')}
                description={t('suspensions.form.modeDurationDesc')}
              />
              <ModeButton
                value="manual"
                icon={<CalendarMonthIcon />}
                label={t('suspensions.form.modeManual')}
                description={t('suspensions.form.modeManualDesc')}
              />
            </Stack>

            <Divider />

            {/* ── Mode : Indéfini ── */}
            {mode === 'indefinite' && (
              <Alert severity="warning" icon={<AllInclusiveIcon />}>
                {t('suspensions.form.indefiniteInfo')}
              </Alert>
            )}

            {/* ── Mode : Durée ── */}
            {mode === 'duration' && (
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <TextField
                  label={t('suspensions.form.durationValue')}
                  type="number"
                  size="small"
                  value={durValue}
                  onChange={(e) => setDurValue(Math.max(1, Number(e.target.value)))}
                  inputProps={{ min: 1 }}
                  sx={{ width: 120 }}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>{t('suspensions.form.durationUnit')}</InputLabel>
                  <Select
                    value={durUnit}
                    label={t('suspensions.form.durationUnit')}
                    onChange={(e: SelectChangeEvent) => setDurUnit(e.target.value as DurUnit)}
                  >
                    <MenuItem value="minutes">{t('suspensions.form.unitMinutes')}</MenuItem>
                    <MenuItem value="hours">{t('suspensions.form.unitHours')}</MenuItem>
                    <MenuItem value="days">{t('suspensions.form.unitDays')}</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            )}

            {/* ── Mode : Programmée ── */}
            {mode === 'manual' && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <DateTimePicker
                  label={t('suspensions.form.start')}
                  value={startAt}
                  onChange={(v) => setStartAt(v)}
                  minDateTime={new Date()}
                  slotProps={{ textField: { size: 'small', fullWidth: true, required: true } }}
                />
                <DateTimePicker
                  label={t('suspensions.form.end')}
                  value={endAt}
                  onChange={(v) => setEndAt(v)}
                  minDateTime={startAt ?? new Date()}
                  slotProps={{ textField: { size: 'small', fullWidth: true, required: true } }}
                />
              </Stack>
            )}

            {/* Erreur de validation */}
            {validErr && (
              <Alert severity="warning">{validErr}</Alert>
            )}

          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!!validErr || submitting}
            color={mode === 'indefinite' ? 'warning' : 'primary'}
          >
            {submitting
              ? <CircularProgress size={18} />
              : mode === 'indefinite'
                ? t('suspensions.form.submitIndefinite')
                : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default SuspensionFormDialog;
